import React, { Component } from 'react';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import b from 'b_';
import TextWithTitle from '../ConsoleFragments/TextWithTitle';
import MoreMenu from '../Menu/MoreMenu';
import BrowserPoliciesStore from '../../stores/BrowserPolicies/BrowserPoliciesStore';
import ConsoleCard from '../ConsoleFragments/ConsoleCard';
import { ConsoleCardHeader, ConsoleTable } from '../ConsoleFragments';
import Button from '../Button';
import DatepickerStore from '../../stores/DatepickerStore';
import SnackStore from '../../stores/SnackStore';
import './AccessCodesTable.css';

const block = b.lock('AccessCodesTable');

class AccessCodesTable extends Component {
    static propTypes = {
        policy: PropTypes.shape({
            id: PropTypes.number.isRequired,
            name: PropTypes.string.isRequired,
            accessCodes: MobxPropTypes.arrayOrObservableArray.isRequired,
        }),
        browserPolicies: BrowserPoliciesStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
        readOnly: PropTypes.bool,
    };

    getExpirationDate = (accessCode) => {
        const { getGmtDate } = this.props.browserPolicies.accessCodesEditStore.datepickerStore;

        if (accessCode.deactivated) {
            return 'Deactivated';
        }
        if (accessCode.neverExpire) {
            return 'Never Expire';
        }
        const expirationDate = getGmtDate(accessCode.expirationDate).format(DatepickerStore.DATE_FORMAT).toUpperCase();
        if (getGmtDate(accessCode.expirationDate).isBefore(getGmtDate(), 'day')) {
            return `Expired ${expirationDate}`;
        }
        if (accessCode.expirationDate) {
            return expirationDate;
        }
        return '';
    };

    copyToClipboard = (accessCode) => {
        const { policy, browserPolicies } = this.props;
        if (browserPolicies.copyTextToClipboard(accessCode.code)) {
            this.props.snack
                .showSuccess(`${policy.name} access code ${accessCode.code} has been copied to your clipboard.`);
            browserPolicies.accessCodesEditStore.menu.close();
        }
    };

    shouldWarn = (accessCode) => {
        const { getGmtDate } = this.props.browserPolicies.accessCodesEditStore.datepickerStore;

        return accessCode.deactivated
            || (!accessCode.neverExpire && getGmtDate(accessCode.expirationDate).isBefore(getGmtDate(), 'day'))
            || (!accessCode.unlimitedActivations && (accessCode.activationsAllowed <= accessCode.activations))
            ? block('RedCell') : '';
    };

    columns = [{
        name: 'access_code',
        title: 'Access Code',
        render: (accessCode) => <TextWithTitle
            text={accessCode.code}
            className={this.shouldWarn(accessCode)}
        />,
    }, {
        name: 'activations_allowed',
        title: 'Activation Limit',
        render: (accessCode) => <TextWithTitle
            text={accessCode.unlimitedActivations ? 'Unlimited' : accessCode.activationsAllowed}
            className={this.shouldWarn(accessCode)}
        />,
    }, {
        name: 'activations',
        title: 'Activations',
        render: (accessCode) => <TextWithTitle
            text={accessCode.activations}
            className={this.shouldWarn(accessCode)}
        />,
    }, {
        name: 'expiration',
        title: 'Expiration',
        render: (accessCode) => <TextWithTitle
            text={this.getExpirationDate(accessCode)}
            className={this.shouldWarn(accessCode)}
        />,
    }, {
        name: 'menu',
        title: '',
        render: (accessCode) => {
            const { browserPolicies, readOnly } = this.props;
            const { accessCodesEditStore } = browserPolicies;
            return <MoreMenu
                menuId={accessCode.id}
                menuStore={accessCodesEditStore.menu}
            >
                {!readOnly ? <MoreMenu.Item
                    icon={'edit'}
                    disabled={accessCode.deactivated}
                    onClick={() => accessCodesEditStore.showEdit(this.props.policy, accessCode)}
                >Edit Access Code…</MoreMenu.Item> : null}
                <MoreMenu.Item
                    icon={'copy'}
                    onClick={() => this.copyToClipboard(accessCode)}
                    disabled={accessCode.deactivated}
                >Copy Access Code</MoreMenu.Item>
                {!readOnly ? <MoreMenu.Item
                    icon={'block'}
                    disabled={accessCode.deactivated}
                    onClick={() => accessCodesEditStore.showDeactivate(this.props.policy, accessCode)}
                >Deactivate Access Code…</MoreMenu.Item> : null}
                {!readOnly ? <MoreMenu.Item
                    icon={'delete'}
                    onClick={() => accessCodesEditStore.showDelete(this.props.policy, accessCode)}
                >Delete Access Code…</MoreMenu.Item> : null}
            </MoreMenu>;
        },
    }];

    render() {
        const { policy, browserPolicies, readOnly } = this.props;
        const { accessCodesEditStore } = browserPolicies;

        return (
            <ConsoleCard className={block('AccessCodesCard')}>
                <ConsoleCardHeader title={`${policy.name} - Access Codes`}>
                    {!readOnly ? <Button
                        raised
                        primary
                        icon="add"
                        className={block('AddCodeButton')}
                        onClick={() => accessCodesEditStore.showCreate(policy)}
                    >NEW CODE</Button> : null}
                </ConsoleCardHeader>
                <ConsoleTable
                    columns={this.columns}
                    items={policy.accessCodes}
                    focusedItemId={accessCodesEditStore.menu.id}
                    cellClassName={block('Cell')}
                />
            </ConsoleCard>
        );
    }
}

export default inject('browserPolicies', 'snack')(observer(AccessCodesTable));
